import { Combobox as BCombobox } from '@base-ui/react/combobox'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { Avatar } from './avatar'
import { Badge } from './badge'
import { formatPhone, PhoneInput, phoneDigits } from './phone-input'

/**
 * A staff member who can be a school's referral contact. Field names are camelCase
 * for the React API; the data-fetching island maps the snake_case API payload
 * (`avatar_url`, `phone_number`) onto this shape.
 */
export interface ReferralContact {
  id: string
  name: string
  title: string | null
  avatarUrl: string | null
  phoneNumber: string | null
}

export interface SchoolReferralContactPickerProps {
  /** The configured per-school contact, or null when the school uses the district default. */
  value: ReferralContact | null
  /** Active staff eligible to be the contact. */
  staff: ReferralContact[]
  /**
   * The district MPOC default(s) a school falls back to when `value` is null:
   * 0 = no default (manual approval), 1 = a named default, >1 = a rotation.
   */
  districtDefaultContacts: ReferralContact[]
  /** Fired when a staff member is chosen, or null to revert to the district default. */
  onSelect: (userId: string | null) => void
  /** Fired when saving a phone number to the contact's profile ("Save to profile"). */
  onSavePhone: (userId: string, digits: string) => void
  /** True while an onSelect / onSavePhone request is in flight. */
  isSaving?: boolean
  disabled?: boolean
  className?: string
}

const DEFAULT_OPTION_ID = '__district_default__'

// A single (non-union) item type keeps Base UI's Combobox generic inference unambiguous.
// The pinned first row (kind 'default', contact null) reverts the school to the district default.
interface ComboItem {
  id: string
  kind: 'default' | 'staff'
  contact: ReferralContact | null
}

// Stable module constant so it isn't a changing dependency of the item memos.
const DEFAULT_ITEM: ComboItem = { id: DEFAULT_OPTION_ID, kind: 'default', contact: null }

export function SchoolReferralContactPicker({
  value,
  staff,
  districtDefaultContacts,
  onSelect,
  onSavePhone,
  isSaving = false,
  disabled = false,
  className,
}: SchoolReferralContactPickerProps) {
  const filter = BCombobox.useFilter({ sensitivity: 'base' })
  const [query, setQuery] = useState('')

  const staffItems = useMemo<ComboItem[]>(
    () => staff.map((contact) => ({ id: contact.id, kind: 'staff', contact })),
    [staff]
  )
  const allItems = useMemo<ComboItem[]>(() => [DEFAULT_ITEM, ...staffItems], [staffItems])

  // Manual filtering keeps the "district default" row pinned regardless of the query.
  const filteredItems = useMemo<ComboItem[]>(() => {
    if (query.trim() === '') return allItems
    return [
      DEFAULT_ITEM,
      ...staffItems.filter((it) => it.contact != null && filter.contains(it.contact.name, query)),
    ]
  }, [allItems, staffItems, query, filter])

  // Base UI tracks the selected object; resolve `value` to a live staff item (null → the
  // default row). A configured contact who is no longer active won't be in `staff`, so
  // Base UI shows the default row while the trigger/note still surface the inactive person.
  const selectedItem: ComboItem =
    value != null ? (staffItems.find((it) => it.id === value.id) ?? DEFAULT_ITEM) : DEFAULT_ITEM

  const valueInactive = value != null && !staff.some((s) => s.id === value.id)

  // The single contact whose phone status we surface below the picker: the configured
  // contact, or the sole district default. A rotation / no-default has no single phone.
  const effectiveContact: ReferralContact | null =
    value ?? (districtDefaultContacts.length === 1 ? (districtDefaultContacts[0] ?? null) : null)

  return (
    <div className={clsx('flex w-full min-w-[20rem] flex-col gap-2', className)}>
      <BCombobox.Root
        items={allItems}
        filteredItems={filteredItems}
        filter={null}
        value={selectedItem}
        onValueChange={(selected: ComboItem | null) =>
          onSelect(selected && selected.kind === 'staff' ? selected.id : null)
        }
        onInputValueChange={(next: string) => setQuery(next)}
        disabled={disabled}
      >
        <BCombobox.Trigger
          className="bg-sand-25 border border-gray-stroke focus:border-forest data-popup-open:border-forest min-h-[3rem] w-full rounded-lg px-3 py-2 focus:ring-0 flex flex-row items-center justify-between gap-2 text-sm data-disabled:opacity-40 data-disabled:cursor-not-allowed"
          aria-label="School referral contact"
        >
          <BCombobox.Value>
            {(selected: ComboItem | null) => (
              <TriggerContent
                selected={selected}
                districtDefaultContacts={districtDefaultContacts}
              />
            )}
          </BCombobox.Value>
          <BCombobox.Icon>
            <i className="fa-solid fa-caret-down text-charcoal-50" aria-hidden="true" />
          </BCombobox.Icon>
        </BCombobox.Trigger>

        <BCombobox.Portal>
          <BCombobox.Positioner align="start" className="outline-none z-10 select-none">
            <BCombobox.Popup className="box-border border rounded-lg border-gray-stroke bg-white shadow-sm flex flex-col w-[var(--anchor-width)] origin-[var(--transform-origin)] overflow-y-auto max-h-[var(--available-height)]">
              <div className="p-2 border-b border-gray-stroke">
                <BCombobox.Input
                  placeholder="Search staff"
                  className="bg-sand-25 border border-gray-stroke focus:border-forest h-9 w-full rounded-lg px-3 text-sm outline-none focus:ring-0 placeholder:text-charcoal-50"
                />
              </div>

              <BCombobox.Empty className="px-4 py-3 text-sm text-charcoal-50">
                No staff found
              </BCombobox.Empty>

              <BCombobox.List className="py-1 overflow-y-auto">
                {(item: ComboItem) => (
                  <BCombobox.Item
                    key={item.id}
                    value={item}
                    className="px-3 py-2 cursor-default select-none outline-none text-sm text-charcoal-75 data-selected:bg-mint-25 data-highlighted:bg-sand-25 flex flex-row items-center justify-between gap-2"
                  >
                    {item.kind === 'staff' && item.contact ? (
                      <StaffRow contact={item.contact} />
                    ) : (
                      <DefaultRow districtDefaultContacts={districtDefaultContacts} />
                    )}
                    <BCombobox.ItemIndicator>
                      <i className="fa-solid fa-check text-forest text-xs" aria-hidden="true" />
                    </BCombobox.ItemIndicator>
                  </BCombobox.Item>
                )}
              </BCombobox.List>
            </BCombobox.Popup>
          </BCombobox.Positioner>
        </BCombobox.Portal>
      </BCombobox.Root>

      <StatusArea
        value={value}
        valueInactive={valueInactive}
        effectiveContact={effectiveContact}
        districtDefaultContacts={districtDefaultContacts}
        onSavePhone={onSavePhone}
        isSaving={isSaving}
      />
    </div>
  )
}

function ContactIdentity({ contact }: { contact: ReferralContact }) {
  return (
    <span className="flex flex-row items-center gap-2 min-w-0 flex-1">
      <Avatar src={contact.avatarUrl ?? ''} alt="" size="sm" className="shrink-0" />
      <span className="flex flex-col min-w-0 text-left">
        <span className="truncate text-charcoal">{contact.name}</span>
        {contact.title ? (
          <span className="text-charcoal-50 text-xs truncate">{contact.title}</span>
        ) : null}
      </span>
    </span>
  )
}

function TriggerContent({
  selected,
  districtDefaultContacts,
}: {
  selected: ComboItem | null
  districtDefaultContacts: ReferralContact[]
}) {
  if (selected && selected.kind === 'staff' && selected.contact) {
    return <ContactIdentity contact={selected.contact} />
  }
  return <DefaultRow districtDefaultContacts={districtDefaultContacts} />
}

function DefaultRow({ districtDefaultContacts }: { districtDefaultContacts: ReferralContact[] }) {
  const soleDefault = districtDefaultContacts.length === 1 ? districtDefaultContacts[0] : null

  if (soleDefault) {
    return (
      <span className="flex flex-row items-center gap-2 min-w-0 flex-1">
        <ContactIdentity contact={soleDefault} />
        <Badge label="District MPOC · default" color="sky" wrapLabel={false} className="shrink-0" />
      </span>
    )
  }

  if (districtDefaultContacts.length > 1) {
    return (
      <span className="flex flex-row items-center gap-2 text-charcoal-50">
        <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
        Rotates across {districtDefaultContacts.length} district MPOCs
      </span>
    )
  }

  return (
    <span className="flex flex-row items-center gap-2 text-charcoal-50">
      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
      No default contact
    </span>
  )
}

function StaffRow({ contact }: { contact: ReferralContact }) {
  return (
    <span className="flex flex-row items-center gap-2 min-w-0 flex-1">
      <ContactIdentity contact={contact} />
      {contact.phoneNumber ? null : (
        <Badge
          label="No phone"
          color="orange"
          leftIcon="phone-slash"
          wrapLabel={false}
          className="shrink-0"
        />
      )}
    </span>
  )
}

function StatusArea({
  value,
  valueInactive,
  effectiveContact,
  districtDefaultContacts,
  onSavePhone,
  isSaving,
}: {
  value: ReferralContact | null
  valueInactive: boolean
  effectiveContact: ReferralContact | null
  districtDefaultContacts: ReferralContact[]
  onSavePhone: (userId: string, digits: string) => void
  isSaving: boolean
}) {
  if (valueInactive && value) {
    return (
      <p className="text-xs text-charcoal-50">
        {value.name} is no longer active — referrals will use the district default.
      </p>
    )
  }

  if (effectiveContact) {
    return effectiveContact.phoneNumber ? (
      <p className="flex items-center gap-1.5 text-xs text-forest">
        <i className="fa-solid fa-circle-check" aria-hidden="true" />
        Phone on file · {formatPhone(effectiveContact.phoneNumber)}
      </p>
    ) : (
      <AddPhonePanel contact={effectiveContact} onSavePhone={onSavePhone} isSaving={isSaving} />
    )
  }

  if (districtDefaultContacts.length === 0) {
    return (
      <p className="text-xs text-charcoal-50">
        No default contact — these referrals will require manual approval.
      </p>
    )
  }

  return null
}

function AddPhonePanel({
  contact,
  onSavePhone,
  isSaving,
}: {
  contact: ReferralContact
  onSavePhone: (userId: string, digits: string) => void
  isSaving: boolean
}) {
  const [entered, setEntered] = useState('')
  const digits = phoneDigits(entered)
  const canSave = digits.length === 10 && !isSaving

  return (
    <div className="rounded-lg border border-gold-50 bg-gold-25 p-3 flex flex-col gap-2">
      <p className="text-sm font-medium text-charcoal">Add a phone number for {contact.name}</p>
      <p className="text-xs text-charcoal-50">
        A phone number is required on auto-approved referrals, and this staff member doesn&apos;t
        have one on their profile yet.
      </p>
      <div className="flex flex-row items-center gap-2">
        <PhoneInput value={entered} onChange={(formatted) => setEntered(formatted)} />
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSavePhone(contact.id, digits)}
          className="shrink-0 rounded-lg bg-forest px-4 h-10 text-sm font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Save to profile
        </button>
      </div>
    </div>
  )
}

SchoolReferralContactPicker.displayName = 'SchoolReferralContactPicker'
